/**
 * screens/modals/ApprovalPolicyModal
 * ──────────────────────────────────────────────────────────────────────────
 * The three thresholds and the base currency.
 *
 * SAVING THESE CREATES RULES. `threshold_minor` and `dual_over_minor` seed
 * approval rules server-side rather than being evaluated as hidden numbers, so
 * after this modal the Approval rules card has new rows. The hint says so,
 * because a settings change that silently rewrites a rule list is exactly the
 * kind of thing an admin should not have to discover.
 *
 * The base currency is only offered while it is UNSET. Changing it later would
 * re-denominate every threshold in the workspace against balances that were
 * never converted (invariant 4), so it is a one-way door and the modal says so.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { ChipRow, Input, Modal } from '../../components';
import { toastPlain } from '../../components/Toast';
import { neutral } from '../../theme/colors';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { formatMinor, parseMajorToMinor } from '../../lib/money';
import { patchPolicy } from '../../services/config/config.service';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export function ApprovalPolicyModal() {
  const { closeModal } = useShell();
  const { policy, optionsByKind, reload } = useConfig();

  const [currency, setCurrency] = useState<string | null>(policy?.base_currency ?? null);
  const [threshold, setThreshold] = useState(
    policy?.threshold_minor && policy.base_currency
      ? formatMinor(policy.threshold_minor, policy.base_currency).replace(/,/g, '')
      : '',
  );
  const [autoUnder, setAutoUnder] = useState(
    policy?.auto_under_minor && policy.base_currency
      ? formatMinor(policy.auto_under_minor, policy.base_currency).replace(/,/g, '')
      : '',
  );
  const [dualOver, setDualOver] = useState(
    policy?.dual_over_minor && policy.base_currency
      ? formatMinor(policy.dual_over_minor, policy.base_currency).replace(/,/g, '')
      : '',
  );

  const currencyLocked = !!policy?.base_currency;

  const currencyChips = useMemo(
    () =>
      optionsByKind.currency
        .filter((option) => option.is_active)
        .map((option) => ({ value: option.value, label: option.value })),
    [optionsByKind],
  );

  const parse = (text: string): string | null =>
    text.trim() && currency ? parseMajorToMinor(text, currency) : null;

  const { submitting, run } = useModalSubmit({
    validate: () => {
      if (!currency) return 'Pick a base currency.';
      for (const [label, text] of [
        ['approval threshold', threshold],
        ['auto-approve floor', autoUnder],
        ['dual-approval ceiling', dualOver],
      ] as const) {
        if (text.trim() && parse(text) === null) {
          return `That is not a valid ${label} for ${currency}.`;
        }
      }
      // A floor above the threshold would mean every spend is both
      // auto-approved and held, which the engine cannot honour.
      const floor = parse(autoUnder);
      const hold = parse(threshold);
      if (floor && hold && BigInt(floor) > BigInt(hold)) {
        return 'The auto-approve floor cannot be above the approval threshold.';
      }
      const ceiling = parse(dualOver);
      if (ceiling && hold && BigInt(ceiling) < BigInt(hold)) {
        return 'The dual-approval ceiling cannot be below the approval threshold.';
      }
      return null;
    },
    submit: () =>
      patchPolicy({
        ...(currencyLocked ? {} : { base_currency: currency! }),
        ...(parse(threshold) ? { threshold_minor: parse(threshold)! } : {}),
        // An explicit null clears the setting; undefined leaves it alone.
        auto_under_minor: autoUnder.trim() ? parse(autoUnder) : null,
        dual_over_minor: dualOver.trim() ? parse(dualOver) : null,
      }),
    title: 'Approval policy saved',
    fallback: 'Rules have been created to match. You can edit them in Settings.',
    onDone: (result) => {
      // The API reports what the save SEEDED. Saying so is what stops the
      // Approval rules card appearing to change on its own.
      const seeded = result.seeded_rules?.length ?? 0;
      if (seeded > 0) {
        toastPlain(
          `${seeded} approval rule${seeded === 1 ? '' : 's'} created`,
          'You can pause or remove them in Settings.',
        );
      }
      void reload();
    },
  });

  return (
    <Modal
      visible
      title="Approval policy"
      hint="Saving the threshold or the ceiling creates approval rules you can then see and edit."
      onClose={closeModal}
      primaryLabel="Save policy"
      onPrimary={run}
      primaryLoading={submitting}
    >
      {currencyLocked ? (
        <View style={styles.lockedBox}>
          <Text style={styles.lockedLabel}>Base currency</Text>
          <Text style={styles.lockedValue}>{policy?.base_currency}</Text>
          <Text style={styles.lockedNote}>
            Fixed once set. Balances are never converted between currencies, so re-denominating the
            thresholds afterwards would compare unlike numbers.
          </Text>
        </View>
      ) : (
        <ChipRow
          label="Base currency"
          required
          hint="Thresholds and charts are denominated in this. It cannot be changed later."
          options={currencyChips}
          value={currency}
          onChange={setCurrency}
        />
      )}

      <Input
        label="Approval over"
        hint="Spends at or above this wait for an approver."
        value={threshold}
        onChangeText={setThreshold}
        placeholder="0.00"
        prefix={currency ?? undefined}
        numeric
      />
      <Input
        label="Auto-approve under"
        hint="Spends below this are never held, whatever the rules say."
        value={autoUnder}
        onChangeText={setAutoUnder}
        placeholder="0.00"
        prefix={currency ?? undefined}
        numeric
      />
      <Input
        label="Dual approval over"
        hint="Above this, a second and different approver is required."
        value={dualOver}
        onChangeText={setDualOver}
        placeholder="0.00"
        prefix={currency ?? undefined}
        numeric
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  lockedBox: { gap: 3 },
  lockedLabel: { ...sans('500'), fontSize: fontSize.label, color: neutral.ink2 },
  lockedValue: { ...sans('600'), fontSize: fontSize.md, color: neutral.ink },
  lockedNote: {
    ...sans('400'),
    fontSize: fontSize.meta,
    lineHeight: leading(fontSize.meta),
    color: neutral.grey600,
  },
});

export default ApprovalPolicyModal;
